import React from 'react';
import { captureException } from '@sentry/react';

interface State {
  error: Error | null;
}

/**
 * 앱 최상위 에러 바운더리 — 카드 하나의 렌더 예외로 React가 화면 전체를 비우는(흰 화면) 대신
 * 안내와 새로고침 버튼을 보여 주고 Sentry에 보고한다.
 * (Sentry 네임스페이스 전체를 가져오면 트리 셰이킹이 막혀 번들이 gzip 48KB 늘었다 — 필요한 함수만 가져온다)
 */
export default class AppErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[APP] 렌더 오류:', error, info.componentStack);
    captureException(error, { extra: { componentStack: info.componentStack } }); // Sentry 미초기화(개발)면 무시된다
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white px-4">
          <div className="max-w-sm text-center">
            <p className="font-semibold text-gray-900 mb-2">화면을 표시하는 중 문제가 생겼습니다.</p>
            <p className="text-sm text-gray-500 mb-6">잠시 후 다시 시도해 주세요. 같은 문제가 반복되면 알려 주세요.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 text-sm min-h-[44px]"
            >
              새로고침
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
